import { NodeProps } from 'reactflow';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { faBolt } from '@fortawesome/pro-solid-svg-icons';
import { AmountBadges } from 'assets/js/pages/engage/workflows/components/AmountBadges';
import * as CanvasNode from 'assets/js/pages/engage/workflows/components/CanvasNode';
import * as NodeBadges from 'assets/js/pages/engage/workflows/components/NodeBadges';
import { StepFormContainer } from 'assets/js/pages/engage/workflows/components/StepFormContainer';
import { useDebouncedAutosave } from 'assets/js/pages/engage/workflows/hooks/useDebouncedAutosave';
import { useSyncFormToNode } from 'assets/js/pages/engage/workflows/hooks/useSyncFormToNode';
import { useTriggerValidationOnDeselection } from 'assets/js/pages/engage/workflows/hooks/useTriggerValidationOnDeselection';
import {
    AnniversaryYearOptions,
    RecurringPlanAnniversaryTriggerFormValues,
    RecurringPlanAnniversaryTriggerNodeData,
} from 'assets/js/pages/engage/workflows/triggers/types';
import { enabledTriggers } from 'assets/js/pages/engage/workflows/utils/starterItems';
import * as Form from 'components/form';
import { InputCurrencyIcon } from 'components/input';
import { WorkflowTriggerTypeEnum } from 'constants/workflows';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { capitalize } from 'lodash';
import { DateTime } from 'luxon';

interface RecurringPlanAnniversaryTriggerProps
    extends NodeProps<RecurringPlanAnniversaryTriggerNodeData> {}
const RecurringPlanAnniversaryTrigger = ({
    id,
    selected,
    data,
}: RecurringPlanAnniversaryTriggerProps) => {
    const { timezone } = useCurrentAccount();
    const { formData } = data;
    const methods = Form.useGivebutterForm<RecurringPlanAnniversaryTriggerFormValues>({
        mode: 'all',
        defaultValues: {
            frequency: formData?.values.frequency || 'any',
            anniversary: formData?.values.anniversary || 'any',
            recurringOperator: formData?.values.recurringOperator || 'gt',
            recurringAmount: formData?.values.recurringAmount || '',
            recurringAmountEnd: formData?.values.recurringAmountEnd || '',
        },
    });
    const {
        control,
        formState: { errors },
        watch,
        path,
        getValues,
        trigger,
    } = methods;
    const isError = Object.keys(errors).length > 0;
    const { recurringOperator } = watch();

    useSyncFormToNode<RecurringPlanAnniversaryTriggerFormValues>({ nodeId: id, control });
    useDebouncedAutosave<RecurringPlanAnniversaryTriggerFormValues>({ methods });
    useTriggerValidationOnDeselection<RecurringPlanAnniversaryTriggerFormValues>({
        trigger,
        selected,
    });

    const selectedTrigger = enabledTriggers.find(
        t => t.slug === WorkflowTriggerTypeEnum.PLAN_ANNIVERSARY,
    );
    if (!selectedTrigger) return;

    return (
        <Form.Provider {...methods}>
            <StepFormContainer>
                <CanvasNode.Root
                    className="max-w-[460px]"
                    isSelected={selected}
                    isError={isError}
                    id={id}
                >
                    <CanvasNode.Container>
                        <CanvasNode.Header>
                            <CanvasNode.Icon icon={faBolt} className="text-yellow-300" />
                            <div className="flex flex-col items-start">
                                <CanvasNode.Title>{selectedTrigger.name}</CanvasNode.Title>
                                {selected ? (
                                    <CanvasNode.Description>
                                        Configure your workflow trigger. Your workflow will initiate
                                        at 10 AM (
                                        {DateTime.now().setZone(timezone).toFormat('ZZZZ')}).
                                    </CanvasNode.Description>
                                ) : null}
                            </div>
                        </CanvasNode.Header>
                        <CanvasNode.Body>
                            <CanvasNode.BodySelected>
                                <Form.Container>
                                    <Form.Label name={path('frequency')}>Frequency</Form.Label>
                                    <Form.SingleSelect control={control} name="frequency">
                                        <SingleSelect.Trigger aria-label="Frequency" />
                                        <SingleSelect.Content>
                                            <SingleSelect.Item value="any">
                                                <SingleSelect.ItemText>Any</SingleSelect.ItemText>
                                            </SingleSelect.Item>
                                            <SingleSelect.Item value="monthly">
                                                <SingleSelect.ItemText>
                                                    Monthly
                                                </SingleSelect.ItemText>
                                            </SingleSelect.Item>
                                            <SingleSelect.Item value="quarterly">
                                                <SingleSelect.ItemText>
                                                    Quarterly
                                                </SingleSelect.ItemText>
                                            </SingleSelect.Item>
                                            <SingleSelect.Item value="yearly">
                                                <SingleSelect.ItemText>
                                                    Yearly
                                                </SingleSelect.ItemText>
                                            </SingleSelect.Item>
                                        </SingleSelect.Content>
                                    </Form.SingleSelect>
                                </Form.Container>
                                <Form.Container>
                                    <Form.Label name={path('anniversary')}>
                                        Anniversary year
                                    </Form.Label>
                                    <Form.SingleSelect control={control} name="anniversary">
                                        <SingleSelect.Trigger aria-label="Anniversary year" />
                                        <SingleSelect.Content>
                                            {sortAnniversaryKeys(
                                                Object.keys(anniversaryYearMap),
                                            ).map(key => (
                                                <SingleSelect.Item value={key} key={key}>
                                                    <SingleSelect.ItemText>
                                                        {anniversaryYearMap[key]}
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                            ))}
                                        </SingleSelect.Content>
                                    </Form.SingleSelect>
                                </Form.Container>
                                <Form.Container>
                                    <Form.Label name={path('recurringAmount')}>
                                        Recurring amount{' '}
                                        <span className="text-gray-400">(optional)</span>
                                    </Form.Label>
                                    <div className="flex items-center">
                                        <Form.SingleSelect
                                            control={control}
                                            name="recurringOperator"
                                            onValueChange={() => trigger()}
                                        >
                                            <SingleSelect.Trigger
                                                aria-label="Recurring amount operator"
                                                className="basis-max-content input-left"
                                            />
                                            <SingleSelect.Content>
                                                <SingleSelect.Item value="gt">
                                                    <SingleSelect.ItemText>
                                                        Greater than
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item value="eq">
                                                    <SingleSelect.ItemText>
                                                        Equals
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item value="lt">
                                                    <SingleSelect.ItemText>
                                                        Less than
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item value="bet">
                                                    <SingleSelect.ItemText>
                                                        Between
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                            </SingleSelect.Content>
                                        </Form.SingleSelect>
                                        <div className="flex min-w-0 items-center gap-2">
                                            <Form.TextInputRoot
                                                control={control}
                                                name="recurringAmount"
                                                inputRootProps={{
                                                    mask: 'decimal',
                                                    className: 'input-right min-w-0',
                                                }}
                                            >
                                                <InputCurrencyIcon />
                                                <Form.TextInputForm />
                                            </Form.TextInputRoot>

                                            {recurringOperator === 'bet' ? (
                                                <>
                                                    <span>and</span>
                                                    <Form.TextInputRoot
                                                        control={control}
                                                        name="recurringAmountEnd"
                                                        rules={{
                                                            required:
                                                                recurringOperator === 'bet' &&
                                                                'End amount is required.',
                                                        }}
                                                        inputRootProps={{
                                                            mask: 'decimal',
                                                            className: 'min-w-0',
                                                        }}
                                                    >
                                                        <InputCurrencyIcon />
                                                        <Form.TextInputForm />
                                                    </Form.TextInputRoot>
                                                </>
                                            ) : null}
                                        </div>
                                    </div>
                                    <Form.Error name="recurringAmountEnd" />
                                </Form.Container>
                            </CanvasNode.BodySelected>
                            <CanvasNode.BodyUnselected>
                                <RecurringPlanAnniversaryFormValuesDisplay values={getValues()} />
                            </CanvasNode.BodyUnselected>
                        </CanvasNode.Body>
                    </CanvasNode.Container>
                </CanvasNode.Root>
            </StepFormContainer>
        </Form.Provider>
    );
};

interface RecurringPlanAnniversaryFormValuesDisplayProps {
    values: RecurringPlanAnniversaryTriggerFormValues;
    showLabel?: boolean;
}
const RecurringPlanAnniversaryFormValuesDisplay = ({
    values,
    showLabel = false,
}: RecurringPlanAnniversaryFormValuesDisplayProps) => {
    const { frequency, anniversary, recurringOperator, recurringAmount, recurringAmountEnd } =
        values;
    const trigger = enabledTriggers.find(t => t.slug === WorkflowTriggerTypeEnum.PLAN_ANNIVERSARY);
    if (!trigger) return null;

    return (
        <div className="flex flex-col gap-2">
            {showLabel ? <p className="text-sm font-medium text-gray-900">{trigger.name}</p> : null}
            <NodeBadges.Root>
                <NodeBadges.Badge>
                    From{' '}
                    <span className="text-gray-900">
                        {frequency === 'any' ? 'any frequency' : capitalize(frequency)}
                    </span>
                </NodeBadges.Badge>
                <NodeBadges.Separator>AND</NodeBadges.Separator>
                <NodeBadges.Badge>
                    <span className="text-gray-900">{anniversaryYearMap[anniversary]}</span>
                </NodeBadges.Badge>

                {recurringAmount ? (
                    <AmountBadges
                        amount={recurringAmount}
                        amountEnd={recurringAmountEnd}
                        operator={recurringOperator}
                    />
                ) : null}
            </NodeBadges.Root>
        </div>
    );
};

const anniversaryYearMap: Record<AnniversaryYearOptions, string> = {
    any: 'Any year',
    '1': '1st Anniversary',
    '2': '2nd Anniversary',
    '3': '3rd Anniversary',
    '4': '4th Anniversary',
    '5': '5th Anniversary',
    '6': '6th Anniversary',
    '7': '7th Anniversary',
    '8': '8th Anniversary',
    '9': '9th Anniversary',
    '10': '10th Anniversary',
};

const sortAnniversaryKeys = (keys: string[]) => {
    return keys.toSorted((_, b) => (isNaN(parseInt(b)) ? 1 : -1)) as AnniversaryYearOptions[];
};

export { RecurringPlanAnniversaryTrigger, RecurringPlanAnniversaryFormValuesDisplay };
